import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import SingleChain from './SingleChain';
import EntityView from '../EntityView';
import CompoundComparison from './CompoundComparison';
import { Button } from 'react-bootstrap';

/**
 * CollapsibleChainGroup - Groups multiple evidence chains that share the same LHS (start) node
 * Shows a collapsed summary view with expand/collapse functionality
 */
const CollapsibleChainGroup = ({
  paths,
  startEntity,
  endEntities,
  isScrolling,
  datasets,
  highlightedDatasets,
  baseIdx,
  hideEndNodes = false,
  isExpanded = false,
  isSingleGroup = false,
  onToggle = () => {},
  activeComparison = null,
  onComparisonToggle = () => {},
  fixedHeaderEntityRef = null,
  scrollContainerRef = null
}) => {
  const [contentHeight, setContentHeight] = useState(null); // Track actual content height for smooth transitions
  const contentRef = useRef(null);
  const groupRef = useRef(null);
  const groupHeaderEntityRef = useRef(null); // Ref for group header entity (LHS)
  const chainRefs = useRef({}); // Refs for each chain row

  const toggleExpanded = () => {
    const willExpand = !isExpanded;

    // When collapsing, measure the current visible/rendered height
    if (!willExpand && contentRef.current) {
      const currentHeight = contentRef.current.scrollHeight;
      setContentHeight(currentHeight);

      // Force a reflow to ensure the height is applied before collapsing
      contentRef.current.offsetHeight;

      // Small delay to allow height to be set, then collapse to 0
      requestAnimationFrame(() => {
        onToggle();
      });
    } else {
      onToggle();
    }

    // Close any active comparison when collapsing the row
    if (!willExpand && activeComparison?.groupIdx === baseIdx) {
      onComparisonToggle(baseIdx, null, null);
    }
  };

  // Handle comparison toggle - scroll THEN notify parent
  const handleComparisonToggle = (chainIdx, edgeIdx) => {
    // Check if this is opening a new comparison (not closing current one)
    const isOpening = edgeIdx !== null &&
                      !(activeComparison?.groupIdx === baseIdx &&
                        activeComparison?.chainIdx === chainIdx &&
                        activeComparison?.edgeIdx === edgeIdx);

    if (isOpening && scrollContainerRef?.current && groupRef.current) {
      const chainElement = chainRefs.current[chainIdx];

      if (chainElement) {
        const modalBody = scrollContainerRef.current;
        const groupElement = groupRef.current;
        const groupHeader = groupElement.querySelector('.ev-ch-group-header');

        if (groupHeader) {
          // Get positions relative to the document
          const getOffsetTop = (element) => {
            let offset = 0;
            let el = element;
            while (el && el !== modalBody) {
              offset += el.offsetTop;
              el = el.offsetParent;
            }
            return offset;
          };

          // Chain element has display:contents, so get the actual chain row element
          const chainRowElement = chainElement.querySelector('.ev-ch-chain-row');
          if (!chainRowElement) {
            console.warn('[CollapsibleChainGroup] Could not find chain row');
            onComparisonToggle(baseIdx, chainIdx, edgeIdx);
            return;
          }

          // Get the chain row's absolute position within modal body
          const chainTop = getOffsetTop(chainRowElement);

          // Get the first chain's position to use as reference
          const firstChainElement = chainRefs.current[paths[0].idx];
          const firstChainRow = firstChainElement?.querySelector('.ev-ch-chain-row');

          if (!firstChainRow) {
            console.warn('[CollapsibleChainGroup] Could not find first chain row');
            onComparisonToggle(baseIdx, chainIdx, edgeIdx);
            return;
          }

          const firstChainTop = getOffsetTop(firstChainRow);

          // Scroll so this chain appears at the same viewport position as the first chain
          // When scrollTop = 0, first chain is at viewport position firstChainTop
          // To put current chain at that same viewport position: scrollTop = chainTop - firstChainTop
          const targetScroll = chainTop - firstChainTop;

          modalBody.scrollTo({
            top: targetScroll,
            behavior: 'smooth'
          });

          // After scroll completes, notify parent to show comparison
          setTimeout(() => {
            onComparisonToggle(baseIdx, chainIdx, edgeIdx);
          }, 300);
          return; // Don't call onComparisonToggle yet
        }
      }
    }

    // If not opening or scroll not needed, notify immediately
    onComparisonToggle(baseIdx, chainIdx, edgeIdx);
  };

  // Find the longest path length and calculate grid columns needed
  // Must match SingleChain's edgeSpan calculation
  const maxPathLength = Math.max(...paths.map(({ path }) => path.filter(el => el.entity).length));
  const visibleNodes = maxPathLength - 2; // Subtract 2 for hideFirstLast
  // Calculate edge span based on chain length (same logic as SingleChain)
  const edgeSpan = maxPathLength === 2 ? 3 : maxPathLength === 3 ? 2 : 1;
  const visibleEdges = visibleNodes + 1;
  const totalColumns = visibleNodes + (visibleEdges * edgeSpan); // nodes + edges*span
  const gridClass = `grid-${totalColumns}`;

  // Measure content height when expanding
  useEffect(() => {
    if (isExpanded && contentRef.current) {
      // Measure actual content height for smooth transitions
      const measuredHeight = contentRef.current.scrollHeight;
      setContentHeight(measuredHeight);

      // Scroll the group into view smoothly when expanding
      // Account for the fixed header by scrolling with offset
      setTimeout(() => {
        const element = groupRef.current;
        if (element) {
          const headerHeight = 120; // Approximate height of fixed header
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerHeight;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100); // Small delay to allow content to start rendering
    }
  }, [isExpanded]);

  // Remeasure content height when a comparison opens/closes in this group
  useEffect(() => {
    if (isExpanded && contentRef.current) {
      const isThisGroupActive = activeComparison?.groupIdx === baseIdx;

      // Small delay to allow comparator to render/unmount
      setTimeout(() => {
        if (contentRef.current) {
          const measuredHeight = contentRef.current.scrollHeight;
          setContentHeight(measuredHeight);
        }
      }, 50);
    }
  }, [activeComparison, isExpanded, baseIdx]);

  // No longer needed - scroll is handled in handleComparisonToggle before notifying parent


  return (
    <div className={`ev-ch-collapsible-group ${isExpanded ? 'expanded' : ''}`} ref={groupRef}>
      <div className="ev-ch-group-header" onClick={isSingleGroup ? undefined : toggleExpanded} style={isSingleGroup ? { cursor: 'default' } : {}}>
        <div className="ev-ch-group-header-content">
          <Button
            variant="link"
            className="ev-ch-collapse-toggle"
            aria-expanded={isExpanded}
            disabled={isSingleGroup}
          >
            <span className="ev-ch-triangle-icon">{isExpanded ? '▼' : '▶'}</span>
          </Button>
          <div className="ev-ch-group-nodes">
            <EntityView showLabel={true}
              entity={startEntity}
              dataset={datasets[startEntity?.dataset]}
              scale="collapsed"
              showCategory={false}
              truncate={false}
              entityImageRef={groupHeaderEntityRef}
            />
          </div>
          <div className="ev-ch-group-summary">
            <span className="ev-ch-group-label">{startEntity?.preferredName || startEntity?.label || startEntity?.name}</span>
            <span className="ev-ch-group-count">{paths.length} paths</span>
          </div>
        </div>
        <div className="ev-ch-group-timeline-connector">
          <div className="ev-ch-timeline-line" />
          <div className="ev-ch-timeline-circle" />
        </div>
      </div>
      <div
        ref={contentRef}
        className={`ev-ch-group-content ${gridClass} ${isExpanded ? 'expanded' : ''}`}
        style={{
          maxHeight: isExpanded ?
            (activeComparison?.groupIdx === baseIdx ? 'none' : // No constraint when comparison active
             contentHeight ? `${contentHeight}px` : '10000px') :
            '0'
        }}
      >
        {paths.map(({ idx, path, settings }, arrayIdx) => {
          const isThisChainActive = activeComparison?.groupIdx === baseIdx &&
                                     activeComparison?.chainIdx === idx;
          const isLastInGroup = arrayIdx === paths.length - 1;
          return (
            <div
              key={idx}
              ref={el => {
                if (el) {
                  chainRefs.current[idx] = el;
                }
              }}
              style={{ display: 'contents' }}
            >
              <SingleChain
                chainIdx={idx}
                path={path}
                scaleFactor={settings.scale}
                datasets={datasets}
                highlightedDatasets={highlightedDatasets}
                hideFirstLast={true}
                actions={{ isScrolling }}
                gridColumns={totalColumns}
                activeComparisonEdgeIdx={isThisChainActive ? activeComparison.edgeIdx : null}
                onComparisonToggle={(edgeIdx) => handleComparisonToggle(idx, edgeIdx)}
                groupHeaderEntityRef={groupHeaderEntityRef}
                fixedHeaderEntityRef={fixedHeaderEntityRef}
                isLastInGroup={isLastInGroup}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

CollapsibleChainGroup.propTypes = {
  paths: PropTypes.arrayOf(PropTypes.shape({
    idx: PropTypes.number.isRequired,
    path: PropTypes.arrayOf(PropTypes.object).isRequired,
    settings: PropTypes.object.isRequired,
    visibleBlocks: PropTypes.number.isRequired
  })).isRequired,
  startEntity: PropTypes.object.isRequired,
  endEntities: PropTypes.arrayOf(PropTypes.object).isRequired,
  isScrolling: PropTypes.bool,
  datasets: PropTypes.object,
  highlightedDatasets: PropTypes.arrayOf(PropTypes.string),
  baseIdx: PropTypes.number,
  hideEndNodes: PropTypes.bool,
  isExpanded: PropTypes.bool,
  isSingleGroup: PropTypes.bool,
  onToggle: PropTypes.func,
  activeComparison: PropTypes.object,
  onComparisonToggle: PropTypes.func,
  fixedHeaderEntityRef: PropTypes.object,
  scrollContainerRef: PropTypes.object
};

export default CollapsibleChainGroup;